/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { fastContext } from "@/shared/state";

export type OnboardingAction = "create" | "create_solo" | "invite" | null;

export type AgencyType = "agency" | "solo_broker";

export interface OnboardingValues {
  selectedAction: OnboardingAction;
  agencyType: AgencyType;
  selectedPlanId: string | null;
  selectedPlanName: string | null;
  selectedBillingFrequency: string | null;
  selectedPlanPrice: number | null;
  selectedPlanKey: string | null;
  agencyId: string | null;
  /**
   * The payer the saved `agencyId` was created for, as a fingerprint.
   *
   * The id outlives the tab's memory -- it is restored from sessionStorage and
   * can arrive by `?agency_id=` -- so without this the checkout had no way to
   * tell whether the office now on screen is the one that id belongs to, and
   * billed a new buyer against the previous purchase's agency (BUG-021).
   */
  agencyPayerKey: string | null;
  agencyName: string | null;
  paymentUrl: string | null;
  inviteTokens: Array<{ agencyId: string; agencyName: string; token: string; role: string }>;
  cpf: string | null;
  creci: string | null;
  serviceArea: string | null;
  agencyFormData: Record<string, string> | null;
  /** Which catalogue segment the plan grid was showing when the tier was picked. */
  selectedSegment: 'solo' | 'agency' | null;
  /**
   * What was bought, frozen when the payment succeeded.
   *
   * The activation screen states these back after the controls that produced
   * them are gone, and the subscription record is still settling behind the
   * webhook at that moment — so they are carried, not re-read.
   */
  checkoutSeats: number | null;
  checkoutTotal: number | null;
  checkoutCycle: 'monthly' | 'yearly' | null;
  /**
   * Free days the purchase started with, 0 when it charged today.
   *
   * The receipt needs it to say what was paid today and when the first
   * charge falls; the total alone reads as "paid" on a trial.
   */
  checkoutTrialDays: number | null;
  /** One entry per tier bought, titled — the activation card reads these. */
  checkoutLines: Array<{ title: string; seats: number }> | null;
  /**
   * Auxiliary users the purchase brought. Carried across the redirect with
   * the rest of the receipt: the activation screen has no catalogue to
   * recompute it from, and the figure is the catalogue's to state.
   */
  checkoutSupportSeats: number | null;
}

/**
 * Default initial values for the onboarding form context.
 */
export const onboardingInitialValues: OnboardingValues = {
  selectedAction: null,
  agencyType: "agency",
  selectedPlanId: null,
  selectedPlanName: null,
  selectedBillingFrequency: null,
  selectedPlanPrice: null,
  selectedPlanKey: null,
  agencyId: null,
  agencyPayerKey: null,
  agencyName: null,
  paymentUrl: null,
  inviteTokens: [],
  cpf: null,
  creci: null,
  serviceArea: null,
  agencyFormData: null,
  selectedSegment: null,
  checkoutSeats: null,
  checkoutTotal: null,
  checkoutCycle: null,
  checkoutTrialDays: null,
  checkoutLines: null,
  checkoutSupportSeats: null,
};

/**
 * Fast-context instance that provides the onboarding form state and updater.
 */
export const onboardingFormContext = fastContext;

/**
 * Typed selector hook that wraps onboardingFormContext.useStore
 * so consumers get proper OnboardingValues inference without manual casts.
 */
export function useOnboardingStore<U>(selector: (values: OnboardingValues) => U): U {
  return onboardingFormContext.useStore((state) => selector(state.values as OnboardingValues));
}

/**
 * Typed access to the raw store getter for imperative reads.
 */
export function useOnboardingStoreGet(): () => { values: OnboardingValues } {
  const get = onboardingFormContext.useStore((state) => state.get);
  return () => ({ values: get().values as OnboardingValues });
}
